<template>
  <Layout title="Agent" description="Manage your Agent, track interactions, and improve client satisfaction.">
    <div>
      <div v-show="tab === 'customers'">
        <!-- Top Bar -->
        <div class="mb-4 d-flex justify-content-between align-items-center">
          <h2 class="h5 fw-semibold" style="padding-left:24px">Agent List</h2>
          <div class="d-flex gap-2 ms-auto" style="margin-right: 10px;">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Search Agents..."
            />
          </div>
          <button class="btn btn" style="background: #c77dff; color:white;" @click="showAdd = true">
            Add Agent
          </button>
        </div>

        <!-- Empty State -->
        <div v-if="filteredCustomers.length == 0" style="padding-left:24px">
          <p>Let's get started by adding your first awesome Agent to the system!</p>
        </div>

        <!-- Customer List -->
        <div
          :class="filteredCustomers.length > 9 ? 'overflow-y-auto' : ''"
          style="max-height: 65vh; overflow-x: hidden; padding-left:24px"
        >
          <div class="row g-3">
            <div
              class="col-md-6 col-lg-4"
              v-for="customer in filteredCustomers"
              :key="customer.id"
            >
              <div class="card h-100 border-0 shadow-sm rounded-4 p-3 hover-effect position-relative">
                <!-- Delete -->
                <button
                  class="btn btn-sm text-danger position-absolute top-2 end-2 m-2 rounded-circle"
                  @click.stop="deleteCustomer(customer.id)"
                  title="Delete"
                >
                  <Trash2 class="icon" :size="18" />
                </button>

                <!-- Edit -->
                <button
                  class="btn btn-sm text-primary position-absolute top-2 end-8 m-2 rounded-circle"
                  @click.stop="openEditModal(customer)"
                  title="Edit"
                >
                  <Edit :size="18" />
                </button>


                <div class="card-body">
                  <!-- Header -->
                  <div class="d-flex align-items-center mb-3 gap-3">
                    <div
                      style="width: 50px; height: 50px; background: linear-gradient(135deg, #cfd9ff, #b1d3ff); border-radius: 50%;"
                    >
                      <div class="d-flex align-items-center justify-content-center h-100 fw-bold text-white">
                        {{ customer.initials }}
                      </div>
                    </div>

                    <div class="flex-grow-1">
                      <h5 class="card-title fw-bold mb-0">{{ customer.name }}</h5>
                    </div>
                  </div>

                  <!-- Contact Info -->
                  <p class="card-text text-dark small d-flex align-items-center mb-1">
                    <NotebookTabs class="icon" :size="18" style="margin-right:5px" /> {{ customer.phone }}
                  </p>
                  <p class="card-text text-dark small d-flex align-items-center mb-1">
                    <Mail class="icon" :size="18" style="margin-right:5px" /> {{ customer.email }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add Customer Modal -->
        <div class="modal fade" tabindex="-1" :class="{ show: showAdd }" style="display:block;" v-if="showAdd">
          <div class="modal-dialog">
            <div class="modal-content border-0 shadow-lg rounded-4">
              <div class="modal-header bg-light">
                <h5 class="modal-title fw-semibold">Add New Client</h5>
                <button type="button" class="btn-close" @click="showAdd = false"></button>
              </div>
              <div class="modal-body">
                <form @submit.prevent="submitCustomer">
                  <div class="mb-3">
                    <label class="form-label fw-medium">Full Name</label>
                    <input v-model="form.name" type="text" class="form-control" placeholder="Enter full name" required />
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-medium">Email</label>
                    <input v-model="form.email" type="email" class="form-control" placeholder="Enter email" required />
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-medium">Contact Number</label>
                    <vue-tel-input
                      ref="phoneInputRef"
                      v-model="form.phone"
                      mode="international"
                      placeholder="Enter contact number"
                      required
                      style="width:100%;"
                    />
                  </div>
                  
                  <h6 class="fw-bold mb-3">Tariff Rates</h6>

                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Trip Rate</label>
                      <input v-model.number="form.tripRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">ERP Rate</label>
                      <input v-model.number="form.ERPRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Parking Rate</label>
                      <input v-model.number="form.ParkingRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Meeting Rate</label>
                      <input v-model.number="form.MeetingRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Midnight Rate</label>
                      <input v-model.number="form.MidnightRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Waiting Rate</label>
                      <input v-model.number="form.WaitingRate" type="number" class="form-control" />
                    </div>
                  </div>
                  <div class="modal-footer border-0">
                    <button type="button" class="btn btn-secondary" @click="showAdd = false">Cancel</button>
                    <button type="submit" class="btn btn-primary" :disabled="isAddingCustomer">
                      <span v-if="!isAddingCustomer">Add Client</span>
                      <span v-else class="spinner-border spinner-border-sm" role="status"></span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <!-- Edit Customer Modal -->
        <div class="modal fade" tabindex="-1" :class="{ show: showEdit }" style="display:block;" v-if="showEdit">
          <div class="modal-dialog">
            <div class="modal-content border-0 shadow-lg rounded-4">
              <div class="modal-header bg-light">
                <h5 class="modal-title fw-semibold">Edit Client</h5>
                <button type="button" class="btn-close" @click="showEdit = false"></button>
              </div>
              <div class="modal-body">
                <form @submit.prevent="submitEditCustomer">
                  <div class="mb-3">
                    <label class="form-label fw-medium">Full Name</label>
                    <input v-model="editForm.name" type="text" class="form-control" placeholder="Enter full name" required />
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-medium">Email</label>
                    <input v-model="editForm.email" type="email" class="form-control" placeholder="Enter email" required />
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-medium">Contact Number</label>
                    <vue-tel-input
                      ref="editPhoneRef"
                      :model-value="editForm.phone"
                      mode="international"
                      placeholder="Enter contact number"
                      required
                      style="width:100%;"
                    />
                  </div>

                  <h6 class="fw-bold mt-3 mb-3">Tariff Rates</h6>

                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Trip Rate</label>
                      <input v-model.number="editForm.tripRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">ERP Rate</label>
                      <input v-model.number="editForm.ERPRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Parking Rate</label>
                      <input v-model.number="editForm.ParkingRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Meeting Rate</label>
                      <input v-model.number="editForm.MeetingRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Midnight Rate</label>
                      <input v-model.number="editForm.MidnightRate" type="number" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Waiting Rate</label>
                      <input v-model.number="editForm.WaitingRate" type="number" class="form-control" />
                    </div>
                  </div>

                  <div class="modal-footer border-0">
                    <button type="button" class="btn btn-secondary" @click="showEdit = false">Cancel</button>
                    <button type="submit" class="btn btn-success" :disabled="isUpdating">
                      <span v-if="!isUpdating">Save Changes</span>
                      <span v-else class="spinner-border spinner-border-sm" role="status"></span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import router from "@/router";
import axios from "axios";
import { NotebookTabs, Mail, Trash2,Edit } from 'lucide-vue-next';
import { VueTelInput } from "vue3-tel-input";
import "vue3-tel-input/dist/vue3-tel-input.css";
import { useToast } from 'vue-toastification'

export default {
  name: 'CrmPage',
  components: { Layout, VueTelInput, NotebookTabs, Mail, Trash2,Edit  },
  data() {
    return {
      tab: 'customers',
      showAdd: false,
      showEdit: false,
      searchQuery: '',
      selectedCustomer: {},
      userID: "",
      token: "",
      customerList: [],
      form: {
        name: "",
        email: "",
        phone: "",
        tripRate: 0,
        ERPRate: 0,
        ParkingRate: 0,
        MeetingRate: 0,
        MidnightRate: 0,
        WaitingRate: 0
      },
      editForm: {
        id: "",
        name: "",
        email: "",
        phone: "",
        tripRate: 0,
        ERPRate: 0,
        ParkingRate: 0,
        MeetingRate: 0,
        MidnightRate: 0,
        WaitingRate: 0
      },
      isAddingCustomer: false,
      isUpdating: false,
    };
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser;
      if (!user) throw new Error('Not logged in');
      const token = await user.getIdToken();
      return { Authorization: token };
    },

    async submitCustomer() {
      if (this.isAddingCustomer) return;
      this.isAddingCustomer = true;

      try {
        const headers = await this.getAuthHeader();

        // ✅ same pattern as the working page
        let rawPhone = this.$refs.phoneInputRef
          ? this.$refs.phoneInputRef.modelValue || this.$refs.phoneInputRef.number
          : this.form.phone;

        if (!rawPhone && this.$refs.phoneInputRef) {
          rawPhone =
            this.$refs.phoneInputRef.phone ||
            this.$refs.phoneInputRef.modelValue ||
            this.form.phone;
        }

        console.log("📞 rawPhone:", rawPhone);

        const payload = {
          email: this.form.email,
          password: this.form.password,
          contactPerson: this.form.name,
          contactNumber: rawPhone,
          tripRate: this.form.tripRate,
          ERPRate: this.form.ERPRate,
          ParkingRate: this.form.ParkingRate,
          MeetingRate: this.form.MeetingRate,
          MidnightRate: this.form.MidnightRate,
          WaitingRate: this.form.WaitingRate,
        };

        const res = await axios.post(`${this.$apiURL}/clients`, payload, { headers });
        console.log(res, res.status)
        if (res.status == 201) {
          this.toast.success("Client created successfully!");
          this.showAdd = false;
          this.form = { name: "", email: "", password: "", phone: "" };
          await this.loadCustomers();
        } else {
          this.toast.error(res.data.error || "Failed to create client.");
        }
      } catch (err) {
        this.toast.error(err.response?.data?.error || "Error creating client.");
      } finally {
        this.isAddingCustomer = false;
      }
    },

    openEditModal(customer) {
      this.selectedCustomer = customer;
      this.editForm = {
        id: customer.id,
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        tripRate: customer.tripRate || 0,
        ERPRate: customer.ERPRate || 0,
        ParkingRate: customer.ParkingRate || 0,
        MeetingRate: customer.MeetingRate || 0,
        MidnightRate: customer.MidnightRate || 0,
        WaitingRate: customer.WaitingRate || 0,
      };
      this.showEdit = true;
    },

    async submitEditCustomer() {
      if (this.isUpdating) return;
      this.isUpdating = true;

      try {
        const headers = await this.getAuthHeader();

        // ✅ Extract phone like the POST method
        let rawPhone = this.$refs.editPhoneRef
          ? this.$refs.editPhoneRef.modelValue || this.$refs.editPhoneRef.number
          : this.editForm.phone;

        if (!rawPhone && this.$refs.editPhoneRef) {
          rawPhone =
            this.$refs.editPhoneRef.phone ||
            this.$refs.editPhoneRef.modelValue ||
            this.editForm.phone;
        }

        console.log("📞 Edited rawPhone:", rawPhone);

        const payload = {
          contactPerson: this.editForm.name,
          email: this.editForm.email,
          contactNumber: rawPhone,
          tripRate: this.editForm.tripRate,
          ERPRate: this.editForm.ERPRate,
          ParkingRate: this.editForm.ParkingRate,
          MeetingRate: this.editForm.MeetingRate,
          MidnightRate: this.editForm.MidnightRate,
          WaitingRate: this.editForm.WaitingRate,
        };

        const res = await axios.put(`${this.$apiURL}/clients/${this.editForm.id}`, payload, { headers });

        if (res.status === 200) {
          this.toast.success("Client updated successfully!");
          this.showEdit = false;
          await this.loadCustomers();
        } else {
          this.toast.error(res.data.error || "Failed to update client.");
        }
      } catch (err) {
        this.toast.error(err.response?.data?.error || "Error updating client.");
      } finally {
        this.isUpdating = false;
      }
    },


    async deleteCustomer(id) {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.delete(`${this.$apiURL}/clients/${id}`, { headers });
        if (res.status === 200) {
          this.customerList = this.customerList.filter(c => c.id !== id);
          this.toast.success("Client deleted");
        } else this.toast.error(res.data.message || "Delete failed");
      } catch (err) {
        this.toast.error("An Error Occurred");
      }
    },

    async checkAuthState() {
      const auth = getAuth();
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            this.token = await user.getIdToken();
            resolve();
          } else {
            router.push("/");
            reject(new Error("No user logged in"));
          }
        });
      });
    },

    async loadCustomers() {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.get(`${this.$apiURL}/clients`, { headers });
        this.customerList = res.data.clients || [];
      } catch (err) {
        console.error("❌ Failed to load customers:", err.message);
      }
    },
    checkPageAccess(allowedRoles = []) {
      const access = this.$store.state.systemAccess || {}

      // check if any allowed role is true
      const hasAccess = allowedRoles.some(role => access[role] === true)

      if (!hasAccess) {
        this.toast.error("Access denied.")
        // 🔹 route them based on their actual role
        if (access.admin) {
          this.$router.push("/crm")
        } else if (access.client) {
          this.$router.push("/booking")
        } else if (access.driver) {
          this.$router.push("/jobManagement")
        } else {
          this.$router.push("/")
        }
      }
    },
  },

  computed: {
    clientsNormalized() {
      const list = this.customerList || [];
      return list.map(c => {
        const name = c.contactPerson || "Unknown";
        const email = c.email || "";
        const phone = c.contactNumber || "";
        const initials = (name || "").split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
        return { ...c, name, email, phone, initials };
      });
    },
    filteredCustomers() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return this.clientsNormalized;
      return this.clientsNormalized.filter(c =>
        [c.name, c.email, c.phone].some(v => (v || "").toLowerCase().includes(q))
      );
    },
  },

  async mounted() {
    this.userID = this.$store.getters.getUserId;
    await this.checkPageAccess(["admin"])
    await this.checkAuthState();
    this.loadCustomers();
  },
};
</script>

<style scoped>
.hover-effect {
  transition: all 0.3s ease-in-out;
}
.hover-effect:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}
</style>
